<template>
  <div>
    <div
      class="register-code__label font-size-12 color-complementary-300 margin-bottom-8 flex-between"
    >
      <span>
        {{ $lang.addRegisterCode }}
      </span>
      <span
        @click.stop="handleOpenMenu"
        class="background-complementary-50 color-complementary-400 border-radius-8 height-28 width-28 flex-center cursor--pointer"
      >
        <mdicon name="plus" size="20" />
      </span>
      <MenuPopup
        :show="showMenu"
        :items="itemButtons"
        :permissions="permissions"
        class="left-0 top-12 z-index-1 padding-top-24 z-index-2"
        @close="handleCloseMenu"
      />
    </div>
    <div
      class="padding-x-12 padding-y-8 background-light-gray border--solid border--complementary-100 border-radius-8 color-complementary-400 font-size-14 flex-between"
    >
      {{ `${count} ${$lang.registerCodeAdded}` }}

      <span @click="handleOpenRegisterCodesPin" class="cursor--pointer">{{
        $lang.viewing
      }}</span>
    </div>
    <Modal
      :show="isResourceUploaderModal"
      :title="$lang.addRegisterCodeWithExcelFile"
      :closeOnBackdrop="false"
      :hasClose="false"
      :hasFooter="false"
      size="sm"
      @close="handleCloseResourceUploaderModal"
    >
      <template #body>
        <div>
          <ResourceUploader
            :url="resourceUploaderUrl"
            :label="$lang.fileUploading"
            :cancelButton="true"
            :hasEditTitle="true"
            uploaderName="term_register_code_import"
            exampleFileName="import_term_register_code_sample"
            submitButtonVariant="filled-primary"
            @close="handleCloseResourceUploaderModal"
          />
        </div>
      </template>
    </Modal>
    <Modal
      :show="isSingularlyModal"
      :title="$lang.addRegisterCodeWithSingularly"
      :closeOnBackdrop="false"
      :hasClose="false"
      :hasFooter="false"
      size="sm"
      @close="handleCloseSingularlyModal"
    >
      <template #body>
        <b-form @submit.prevent="handleSubmitAddSingularly">
          <Input
            :label="$lang.exclusiveRegisterCode"
            :placeholder="$lang.exclusiveRegisterCode"
            :value="form.exclusiveRegisterCode"
            name="exclusiveRegisterCode"
            id="exclusiveRegisterCode"
            type="text"
            :maxLength="lengthNumber10"
            :vuelidateObj="$v.form.exclusiveRegisterCode"
            @input="input"
            @focus="resetValidation"
            @blur="checkValidation"
          />
          <Input
            v-if="shouldCheckNationalCode"
            :label="$lang.nationalCode"
            :placeholder="$lang.nationalCode"
            :value="form.nationalCode"
            name="nationalCode"
            id="nationalCode"
            type="text"
            :maxLength="lengthNumber10"
            :vuelidateObj="$v.form.nationalCode"
            @input="input"
            @focus="resetValidation"
            @blur="checkValidation"
          />
          <div class="flex-end padding-0 margin-top-32">
            <Button
              class="margin-left-8"
              contentClass="justify-content-center"
              variant="filled-complementary"
              :text="$lang.cancel"
              @click="handleCloseSingularlyModal"
            />
            <Button
              type="submit"
              contentClass="justify-content-center"
              variant="filled-primary"
              :text="$lang.submit"
              :loading="singularlyLoading"
            />
          </div>
        </b-form>
      </template>
    </Modal>
    <RegisterCodeListPin
      :visible="isRegisterCodesPin"
      @close="handleCloseRegisterCodesPin"
    />
  </div>
</template>

<script>
import inputMixin from "@/mixin/inputMixin.vue";
import MenuPopup from "@/components/re-design/modal/MenuPopup.vue";
import Modal from "@/components/re-design/modal/Modal.vue";
import ResourceUploader from "@/components/re-design/uploader/ResourceUploader.vue";
import { Input, Button } from "@/components/re-design/forms";
import RegisterCodeListPin from "./RegisterCodeListPin.vue";
import {
  required,
  minLength,
  maxLength,
  numeric,
} from "vuelidate/lib/validators";
import { pathOr } from "ramda";
import { generateApiUrl, generateError, toastAlert } from "@/shared/utils";
import { addTermRegisterCode } from "@/modules/profile/services";
export default {
  mixins: [inputMixin],
  inject: ["TERM_CODE", "TERM_DATA"],
  components: {
    MenuPopup,
    Modal,
    ResourceUploader,
    Input,
    Button,
    RegisterCodeListPin,
  },
  validations() {
    return {
      form: {
        exclusiveRegisterCode: { required },
        nationalCode: {
          numeric,
          minLength: minLength(this.lengthNumber10),
          maxLength: maxLength(this.lengthNumber10),
        },
      },
    };
  },
  data() {
    return {
      form: {
        exclusiveRegisterCode: null,
        nationalCode: null,
      },
      showMenu: false,
      isResourceUploaderModal: false,
      isSingularlyModal: false,
      singularlyLoading: false,
      isRegisterCodesPin: false,
    };
  },
  computed: {
    itemButtons() {
      return [
        {
          caption: this.$lang.singularly,
          show: true,
          permissions: ["admin", "edit"],
          loading: false,
          click: this.handleOpenSingularlyModal,
        },
        {
          caption: this.$lang.withExcelFile,
          show: true,
          permissions: ["admin", "edit"],
          loading: false,
          click: this.handleOpenResourceUploaderModal,
        },
      ];
    },
    resourceUploaderUrl() {
      return generateApiUrl(
        ["profile", "v2", "termImportRegisterCode"],
        [this.TERM_CODE]
      );
    },
    permissions() {
      return pathOr([], ["permissions"], this.TERM_DATA);
    },
    count() {
      return pathOr(0, ["term_register_code_count"], this.TERM_DATA);
    },
    shouldCheckNationalCode() {
      return pathOr(false, ["link_check_national_code"], this.TERM_DATA);
    },
  },
  methods: {
    handleOpenMenu() {
      this.showMenu = true;
    },
    handleCloseMenu() {
      this.showMenu = false;
    },
    handleOpenResourceUploaderModal() {
      this.isResourceUploaderModal = true;
    },
    handleCloseResourceUploaderModal() {
      this.isResourceUploaderModal = false;
    },
    handleOpenSingularlyModal() {
      this.isSingularlyModal = true;
    },
    handleCloseSingularlyModal() {
      this.isSingularlyModal = false;
      this.resetForm();
    },
    async handleSubmitAddSingularly() {
      this.$v.$touch();
      if (this.$v.$anyError) return;

      try {
        this.singularlyLoading = true;
        const { exclusiveRegisterCode, nationalCode } = this.form;
        const response = await addTermRegisterCode(
          this.TERM_CODE,
          exclusiveRegisterCode,
          nationalCode
        );
        const { message } = response;
        toastAlert(message);
        this.handleCloseSingularlyModal();
      } catch (error) {
        generateError(error);
      } finally {
        this.singularlyLoading = false;
      }
    },
    handleOpenRegisterCodesPin() {
      this.isRegisterCodesPin = true;
    },
    handleCloseRegisterCodesPin() {
      this.isRegisterCodesPin = false;
    },
  },
};
</script>
